import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaArrowLeft, FaUser, FaStethoscope, FaCreditCard, FaSearch, FaRedo } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';

import './NewAppointment.css';
import ENDPOINTS, { API_BASE_URL } from '../../Constants/ApiEndpoints';
import { doctorsNameToIdMap } from '../../Constants/DoctorsNameToIdMap';
import CameraCapture from '../../CommonComponents/CameraCapture';
import { toast } from 'react-toastify';
import { useAuth } from '../../UserAccounts/AuthProvider';

const days = Array.from({ length: 31 }, (_, i) => i + 1);
const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const years = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

const NewAppointment = ({ onBack }) => {
    const location = useLocation();
    const wrapperRef = useRef(null);
    const { userData } = useAuth();
    const [billItems, setBillItems] = useState([]);
    const [image, setImage] = useState(null);
    const [showAdditional, setShowAdditional] = useState(true);
    const [lastChangedBy, setLastChangedBy] = useState(null);

    const [searchResults, setSearchResults] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const [patientIdentification, setPatientIdentification] = useState('MR No');

    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [billClearanceData, setBillClearanceData] = useState(null);
    const [defaultFeeFromBackened, setDefaultFeeFromBackened] = useState(150);

    const initialPatientPayload = {
        title: '',
        photo: '',
        patientName: '',
        gender: '',
        phoneNumber: '',
        aadharNumber: '',
        clinicId: '',
        age: '',
        address: '',
        mailId: '',
        type: 'OUT_PATIENT',
        dateOfBirth: '',
        patientIdentification: 'MR No',
        mrNumber: '',
    };

    const initialAppointmentPayload = {
        mrNumber: '',
        doctorId: '',
        appointmentDate: new Date().toISOString().split('T')[0],
        patientType: 'OUT_PATIENT',
        emergency: 'FALSE',
        bookingType: 'PHONE',
        timeSlot: '',
        appointmentstatus: 'SCHEDULED',
    };

    const initialPaymentPayload = {
        id: '',
        appointmentId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID',
    };

    const initialInvoicePayload = {
        totalAmount: '150',
        id: '',
        appointmentId: '',
        amount: '',
        paymentMethod: '',
        invoiceName: 'Appointment Fees',
        invoiceDescription: 'Consultation Fees',
        paymentStatus: 'UNPAID',
    };

    const [patientPayload, setPatientPayload] = useState({
        title: '',
        photo: '',
        patientName: '',
        gender: '',
        phoneNumber: '',
        aadharNumber: '',
        clinicId: '',
        age: '',
        address: '',
        mailId: '',
        type: 'OUT_PATIENT',
        dateOfBirth: '',
        patientIdentification: 'MR No',
        mrNumber: '',
    });

    const [appointmentPayload, setAppointmentPayload] = useState({
        mrNumber: '',
        doctorId: '',
        appointmentDate: new Date().toISOString().split('T')[0],
        patientType: 'OUT_PATIENT',
        emergency: 'FALSE',
        bookingType: 'PHONE',
        timeSlot: '',
        appointmentstatus: 'SCHEDULED',
    });

    const [paymentPayload, setPaymentPayload] = useState({
        id: '',
        appointmentId: '',
        amount: '',
        paymentMethod: '',
        paymentStatus: 'UNPAID'
    });

    const [invoicePayload, setInvoicePayload] = useState({
        totalAmount: '150',
        id: '',
        appointmentId: '',
        amount: '',
        paymentMethod: '',
        invoiceName: 'Appointment Fees',
        invoiceDescription: 'Consultation Fees',
        paymentStatus: 'UNPAID'
    });

    useEffect(() => {
        const prefill = location.state?.patient || location.state?.patientData;
        if (prefill) {
            setPatientPayload(prev => ({
                ...prev,
                title: prefill.title || (prefill.gender?.toLowerCase() === 'female' ? 'Mrs' : 'Mr'),
                patientName: prefill.patientName || prefill.name || '',
                gender: prefill.gender || '',
                phoneNumber: prefill.phoneNumber || prefill.phone || prefill.mobileNumber || prefill.contactNumber || '',
                aadharNumber: prefill.aadharNumber || '',
                age: prefill.age || '',
                ageYears: prefill.age || '',
                address: prefill.address || '',
                mailId: prefill.mailId || prefill.email || '',
                mrNumber: prefill.mrNumber || '',
                photo: prefill.photo || ''
            }));
            if (prefill.mrNumber) {
                setAppointmentPayload(prev => ({
                    ...prev,
                    mrNumber: prefill.mrNumber
                }));
            }
        }
    }, [location.state]);

    const validate = () => {
        const newErrors = {};

        if (!patientPayload.title) newErrors.title = 'Please select "Title"';
        if (!patientPayload.patientName) newErrors.patientName = 'Please enter "Patient Name"';
        if (!patientPayload.gender) newErrors.gender = 'Please select "Gender"';
        if (!patientPayload.ageYears && !patientPayload.dobYear) newErrors.ageYears = 'Please enter "Age" or "Date of Birth"';
        if (!appointmentPayload.doctorId) newErrors.doctor = 'Please select a Doctor';
        if (!appointmentPayload.bookingType) newErrors.bookingType = 'Please select "Booking Type"';
        if (!appointmentPayload.appointmentDate) newErrors.appointmentDate = 'Please select "Appointment Date"';
        if (!appointmentPayload.timeSlot) newErrors.timeSlot = 'Please select "Time Slot"';
        if (!appointmentPayload.emergency) newErrors.emergency = 'Please select "Emergency" status';
        if (!invoicePayload.totalAmount) newErrors.totalAmount = 'Please enter "Appointment Fee"';

        if (Object.keys(newErrors).length > 0) {
            Object.values(newErrors).forEach((msg) => toast.error(msg));
            return false;
        }

        return true;
    };

    const handleReset = () => {
        setPatientPayload(initialPatientPayload);
        setAppointmentPayload(initialAppointmentPayload);
        setPaymentPayload(initialPaymentPayload);
        setInvoicePayload((prev) => ({
            ...initialInvoicePayload,
            totalAmount: prev.totalAmount,
        }));
        setImage(null);
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        setPatientPayload((prev) => ({
            ...prev,
            [field]: value
        }));

        if (field === 'mrNumber') {
            setAppointmentPayload((prev) => ({
                ...prev,
                mrNumber: value
            }));
        }
    };

    const handleChangeAppointment = (field) => (e) => {
        const value = e.target.value;
        setAppointmentPayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleChangeInvoice = (field) => (e) => {
        const value = e.target.value;
        setInvoicePayload((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const getPlaceholder = (identification) => {
        switch (identification) {
            case 'MR No': return 'Search by Patient MR Number...';
            case 'Phone': return 'Search by Patient Phone Number...';
            case 'Aadhar': return 'Search by Aadhar Number...';
            case 'Clinic Id': return 'Search by Clinic ID...';
            case 'Name': return 'Search by Patient Name...';
            default: return 'Enter Patient Details...';
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const query = patientPayload.mrNumber ? patientPayload.mrNumber.trim() : '';
            if (query.length > 1) {
                const encodedQuery = encodeURIComponent(query);
                let searchUrl = '';
                switch (patientPayload.patientIdentification) {
                    case 'MR No':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByMRNumber/${encodedQuery}`;
                        break;
                    case 'Phone':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsByPhoneNumber/${encodedQuery}`;
                        break;
                    case 'Aadhar':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByAadhar/${encodedQuery}`;
                        break;
                    case 'Clinic Id':
                        searchUrl = `${API_BASE_URL}/getPrescriptionsByClinicId/${encodedQuery}`;
                        break;
                    case 'Name':
                        searchUrl = `${API_BASE_URL}/getPatientDetailsBypatientName/${encodedQuery}`;
                        break;
                    default:
                        searchUrl = '';
                }

                if (searchUrl) {
                    axios
                        .get(searchUrl)
                        .then((res) => {
                            const data = res.data;
                            setSearchResults(Array.isArray(data) ? data : []);
                            setShowSuggestions(true);
                        })
                        .catch((err) => {
                            console.error("Search failed", err);
                            setSearchResults([]);
                            setShowSuggestions(true);
                        });
                }
            } else {
                setShowSuggestions(false);
                setSearchResults([]);
            }
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [patientPayload.mrNumber, patientPayload.patientIdentification]);

    const handlePhotoCapture = (base64Image) => {
        setPatientPayload(prev => ({ ...prev, photo: base64Image }));
    };

    const getHourFrom12Hour = (time12h) => {
        if (!time12h) return 0;
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':').map(Number);
        if (modifier === 'AM' && hours === 12) hours = 0;
        if (modifier === 'PM' && hours !== 12) hours += 12;
        return hours;
    };

    const calculateFeeByTimeSlot = (timeSlot, defaultFee) => {
        if (!timeSlot) return defaultFee;
        const hour = getHourFrom12Hour(timeSlot);
        if (hour >= 20 && hour < 14) return Number(defaultFee);
        if (hour >= 14 && hour < 16) return Number(defaultFee) + 50;
        if (hour >= 16 && hour < 20) return Number(defaultFee);
        return defaultFee;
    };

    const calculateDOBAndAge = (day, monthName, year) => {
        if (!day || !monthName || !year) return {};

        let dayNum = parseInt(day, 10);
        const monthIndex = months.indexOf(monthName);
        const yearNum = parseInt(year, 10);

        const maxDayInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
        if (dayNum > maxDayInMonth) {
            dayNum = maxDayInMonth;
        }

        const dobString = `${yearNum}-${String(monthIndex + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        const today = new Date();

        let ageYears = today.getFullYear() - yearNum;
        let ageMonths = today.getMonth() - monthIndex;
        let ageDays = today.getDate() - dayNum;

        if (ageDays < 0) {
            ageMonths--;
            const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            ageDays += prevMonth.getDate();
        }

        if (ageMonths < 0) {
            ageYears--;
            ageMonths += 12;
        }

        return {
            dateOfBirth: dobString,
            ageYears,
            ageMonths,
            ageDays,
            age: ageYears,
            dobDay: dayNum.toString(),
        };
    };

    const formatInvoiceType = (type) => {
        if (!type || typeof type !== 'string') return 'Invoice';
        const firstPart = type.split('_')[0];
        return firstPart.charAt(0).toUpperCase() + firstPart.slice(1).toLowerCase();
    };

    const calculateDOBFromAge = (years, monthsValue, daysValue) => {
        if (isNaN(years) || isNaN(monthsValue) || isNaN(daysValue)) return {};

        const today = new Date();
        const dob = new Date(
            today.getFullYear() - years,
            today.getMonth() - monthsValue,
            today.getDate() - daysValue
        );

        const dobDay = dob.getUTCDate();
        const dobMonth = months[dob.getUTCMonth()];
        const dobYear = dob.getUTCFullYear();

        return {
            dobDay: dobDay.toString(),
            dobMonth: dobMonth,
            dobYear: dobYear.toString(),
            dateOfBirth: `${dobYear}-${String(dob.getUTCMonth() + 1).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`
        };
    };

    const handleDOBChange = (field) => (e) => {
        const updated = { ...patientPayload, [field]: e.target.value };
        const calc = calculateDOBAndAge(updated.dobDay, updated.dobMonth, updated.dobYear);

        setLastChangedBy('dob');
        setPatientPayload(prev => ({ ...updated, ...calc }));
    };

    useEffect(() => {
        if (lastChangedBy === 'age') {
            const { ageYears, ageMonths, ageDays } = patientPayload;
            const allFilled = ageYears !== '' && ageMonths !== '' && ageDays !== '';
            if (allFilled) {
                const calc = calculateDOBFromAge(
                    parseInt(ageYears),
                    parseInt(ageMonths),
                    parseInt(ageDays)
                );
                setPatientPayload(prev => ({ ...prev, ...calc }));
            }
        }
    }, [patientPayload.ageYears, patientPayload.ageMonths, patientPayload.ageDays]);

    const handleScheduleAppointment = async () => {
        if (!validate()) return;

        const cleanObject = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => {
                    const trimmedValue = typeof value === 'string' ? value.trim() : value;
                    return [key, trimmedValue === '' ? null : trimmedValue];
                })
            );

        const cleanedPatient = cleanObject(patientPayload);
        const cleanedAppointment = cleanObject(appointmentPayload);
        const cleanedPayment = cleanObject(paymentPayload);
        const cleanedInvoice = cleanObject(invoicePayload);

        const now = new Date();
        const pad = (num) => String(num).padStart(2, '0');
        const formattedDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

        const body = {
            patientPayload: {
                ...cleanedPatient,
                age: patientPayload.ageYears || patientPayload.age || 0,
            },
            appointmentPayload: {
                ...cleanedAppointment,
                timestamp: formattedDateTime,
                bookedBy: userData?.username || 'Reception',
            },
            paymentPayload: {
                ...cleanedPayment,
                appointmentId: cleanedAppointment.appointmentId || null,
                amount: cleanedPayment.amount || 0,
            },
            invoicePayload: {
                ...cleanedInvoice,
                appointmentId: cleanedAppointment.appointmentId || null,
                totalAmount: cleanedInvoice.totalAmount || 0,
            },
        };

        try {
            const res = await axios.post(ENDPOINTS.REGISTER_AND_SCHEDULE_APPOINTMENT, body);
            if (res.status === 200) {
                toast.success("Appointment Scheduled successfully!");
                setTimeout(() => {
                    onBack();
                }, 1500);
            } else {
                toast.error("Failed to Schedule Appointment");
            }
        } catch (error) {
            console.error("Error Scheduling Appointment:", error);
            toast.error("Error Scheduling Appointment");
        }
    };

    return (
        <div className="form-container new-appointment-page">
            {/* Top Breadcrumb Bar */}
            <div className="breadcrumb">
                <div className="breadcrumb-title">
                    Dashboard / <strong>New Appointment Registration</strong>
                </div>
                <button onClick={onBack} className="custom-back-button">
                    <FaArrowLeft /> Back to List
                </button>
            </div>

            {/* Fused Quick Patient Search Banner */}
            <div className="mr-search">
                <div className="fused-search-group">
                    <select value={patientPayload.patientIdentification} onChange={handleChange('patientIdentification')}>
                        <option value="MR No">MR No</option>
                        <option value="Phone">Phone</option>
                        <option value="Name">Name</option>
                    </select>

                    <div className="find-patient-input-wrapper">
                        <input
                            type="text"
                            placeholder={getPlaceholder(patientPayload.patientIdentification)}
                            value={patientPayload.mrNumber}
                            onChange={handleChange('mrNumber')}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        />

                        {showSuggestions && (
                            <ul className="suggestions-dropdown">
                                {searchResults.length > 0 ? (
                                    searchResults.map((patient, idx) => (
                                        <li
                                            key={idx}
                                            onClick={async () => {
                                                setPatientPayload((prev) => ({
                                                    ...prev,
                                                    mrNumber: patient.mrNumber || '',
                                                    patientName: patient.patientName || '',
                                                    photo: patient.photo || '',
                                                    gender: patient.gender || '',
                                                    phoneNumber: patient.phoneNumber || '',
                                                    ageYears: patient.age || '',
                                                    mailId: patient.mailId || '',
                                                    address: patient.address || '',
                                                    aadharNumber: patient.aadharNumber || '',
                                                    clinicId: patient.clinicId || '',
                                                    title: patient.title || '',
                                                    doctor: patient.doctor || '',
                                                    dobDay: patient.dobDay || '',
                                                    dobMonth: patient.dobMonth || '',
                                                    dobYear: patient.dobYear || '',
                                                    ageMonths: patient.ageMonths || '',
                                                    ageDays: patient.ageDays || '',
                                                    dateOfBirth: patient.dateOfBirth || '',
                                                    doctorName: patient.doctorName || '',
                                                }));
                                                if (patient.photo) {
                                                    setImage(`data:image/jpeg;base64,${patient.photo}`);
                                                }
                                                setAppointmentPayload((prev) => ({
                                                    ...prev,
                                                    mrNumber: patient.mrNumber || '',
                                                }));

                                                try {
                                                    const response = await axios.get(
                                                        `${API_BASE_URL}/getDefaultAppointmentFee/${patient.mrNumber}`
                                                    );
                                                    setDefaultFeeFromBackened(response.data);
                                                    setInvoicePayload((prev) => ({
                                                        ...prev,
                                                        totalAmount: response.data,
                                                    }));
                                                } catch (error) {
                                                    console.error('Error fetching appointment fee:', error);
                                                }

                                                if (patientPayload.patientIdentification === 'MR No') {
                                                    try {
                                                        const billClearanceResponse = await axios.get(`${ENDPOINTS.GET_BILL_CLEARANCE}/${patient.mrNumber}`);
                                                        setBillClearanceData(billClearanceResponse.data);
                                                    } catch (error) {
                                                        console.error("Failed to fetch bill clearance:", error);
                                                    }
                                                }
                                                setShowSuggestions(false);
                                            }}
                                        >
                                            <strong>{patient.mrNumber}</strong> ({patient.patientName}) - {patient.phoneNumber}
                                        </li>
                                    ))
                                ) : (
                                    <li className="no-results">No matching patients found</li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>

                <button className="alt-s hide-sm">ALT + S</button>
                <button className="reset" onClick={handleReset} title="Reset Form">
                    <FaRedo size={12} /> Reset
                </button>
            </div>

            {/* Bill Clearance Alert */}
            {billClearanceData && (
                <div className="bill-clearance-box">
                    <h4>⚠️ Bill Clearance Notice</h4>
                    <p>Total Outstanding Due Amount: <strong>₹{billClearanceData.totalDueAmount}</strong></p>
                    {billClearanceData?.invoices?.length > 0 && (
                        <p>
                            Pending Invoices:{' '}
                            {[...new Set(
                                billClearanceData.invoices.map((invoice) =>
                                    formatInvoiceType(invoice.invoiceType)
                                )
                            )].join(', ')}
                        </p>
                    )}
                </div>
            )}

            {/* Profile Avatar Capture */}
            <CameraCapture image={image} setImage={setImage} onCapture={handlePhotoCapture} />

            {/* Card 1: Patient Personal Information */}
            <div className="form-body">
                <div className="form-body-title">
                    <FaUser size={15} color="#2563eb" /> Patient Personal Information
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>* Full Name</label>
                        <div className="input-pair">
                            <select value={patientPayload.title} onChange={handleChange('title')}>
                                <option value="">Title</option>
                                <option>Mr</option>
                                <option>Mrs</option>
                                <option>Miss</option>
                                <option>Dr</option>
                            </select>
                            <input type="text" placeholder="Patient Full Name" value={patientPayload.patientName} onChange={handleChange('patientName')} />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>* Gender</label>
                        <select value={patientPayload.gender} onChange={handleChange('gender')}>
                            <option value="">Select Gender</option>
                            <option>Male</option>
                            <option>Female</option>
                            <option>Other</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>* Phone Number</label>
                        <input type="text" placeholder="Mobile Number" value={patientPayload.phoneNumber} onChange={handleChange('phoneNumber')} />
                    </div>

                    <div className="form-group">
                        <label>Aadhar Number</label>
                        <input type="text" placeholder="12-digit Aadhar" value={patientPayload.aadharNumber} onChange={handleChange('aadharNumber')} />
                    </div>

                    <div className="form-group">
                        <label>Clinic ID</label>
                        <input type="text" placeholder="Clinic ID" value={patientPayload.clinicId} onChange={handleChange('clinicId')} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group wide">
                        <label>* Date Of Birth / Age</label>
                        <div className="dob-inputs">
                            <select value={patientPayload.dobDay} onChange={handleDOBChange('dobDay')}>
                                <option value="">Day</option>
                                {days.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <select value={patientPayload.dobMonth} onChange={handleDOBChange('dobMonth')}>
                                <option value="">Month</option>
                                {months.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                            <select value={patientPayload.dobYear} onChange={handleDOBChange('dobYear')}>
                                <option value="">Year</option>
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <span style={{ fontWeight: 600, color: '#64748b', margin: '0 4px' }}>(or) Age:</span>
                            <input type="number" placeholder="Yrs" value={patientPayload.ageYears} onChange={handleChange('ageYears')} />
                            <input type="number" placeholder="Mos" value={patientPayload.ageMonths} onChange={handleChange('ageMonths')} />
                            <input type="number" placeholder="Days" value={patientPayload.ageDays} onChange={handleChange('ageDays')} />
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group wide">
                        <label>Residential Address</label>
                        <input type="text" placeholder="Complete Street Address, City, Pincode" value={patientPayload.address} onChange={handleChange('address')} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group wide">
                        <label>Email Address</label>
                        <input type="email" placeholder="patient@example.com" value={patientPayload.mailId} onChange={handleChange('mailId')} />
                    </div>
                </div>
            </div>

            {/* Card 2: Clinical Appointment Details */}
            <div className="form-body">
                <div className="form-body-title">
                    <FaStethoscope size={15} color="#2563eb" /> Clinical Appointment Details
                </div>

                <div className="form-row">
                    <div className="form-group flex-grow">
                        <label>* Select Doctor</label>
                        <select
                            className="select-doctor-dropdown"
                            value={
                                Object.keys(doctorsNameToIdMap).find(
                                    key => doctorsNameToIdMap[key] === appointmentPayload.doctorId
                                ) || ''
                            }
                            onChange={(e) => {
                                const selectedName = e.target.value;
                                const doctorId = doctorsNameToIdMap[selectedName];
                                if (doctorId) {
                                    setAppointmentPayload(prev => ({
                                        ...prev,
                                        doctorId: doctorId
                                    }));
                                }
                            }}
                        >
                            <option value="">Select Doctor</option>
                            <option value="Dr. Vishwanath">Dr. Vishwanath</option>
                            <option value="Dr. Sushma">Dr. Sushma</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>* Booking Type</label>
                        <select className="select-bookingtype-dropdown" value={appointmentPayload.bookingType} onChange={handleChangeAppointment('bookingType')}>
                            <option value="PHONE">Phone Booking</option>
                            <option value="WALKIN">Walk-in Direct</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>* Appointment Date</label>
                        <div
                            className="calendar-input-group"
                            onClick={(e) => {
                                const inputEl = e.currentTarget.querySelector('input');
                                if (inputEl && inputEl.showPicker) inputEl.showPicker();
                            }}
                        >
                            <input
                                id="appointmentDate"
                                type="date"
                                className="date-picker"
                                value={appointmentPayload.appointmentDate}
                                onChange={handleChangeAppointment('appointmentDate')}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>* Select Time Slot</label>
                        <select
                            className="select-slot-dropdown"
                            disabled={
                                new Date(appointmentPayload.appointmentDate) <
                                new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            value={appointmentPayload.timeSlot}
                            onChange={(e) => {
                                const newTimeSlot = e.target.value;
                                handleChangeAppointment('timeSlot')(e);
                                setInvoicePayload((prev) => ({
                                    ...prev,
                                    totalAmount: calculateFeeByTimeSlot(newTimeSlot, defaultFeeFromBackened),
                                }));
                            }}
                        >
                            <option value="" disabled>Select Time Slot</option>
                            {Array.from({ length: 24 * 6 }, (_, i) => {
                                const hours = Math.floor(i / 6);
                                const minutes = (i % 6) * 10;

                                const now = new Date();
                                const selectedDate = new Date(appointmentPayload.appointmentDate);

                                const todayStart = new Date();
                                todayStart.setHours(0, 0, 0, 0);

                                const selectedDayStart = new Date(selectedDate);
                                selectedDayStart.setHours(0, 0, 0, 0);

                                const isToday = selectedDayStart.getTime() === todayStart.getTime();
                                const isPast = selectedDayStart < todayStart;

                                if (isPast) return null;

                                const slotMinutes = hours * 60 + minutes;
                                const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

                                if (isToday && slotMinutes <= currentTotalMinutes) return null;

                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                const hour12 = hours % 12 === 0 ? 12 : hours % 12;
                                const timeLabel = `${hour12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                                const timeValue = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                                return (
                                    <option key={timeValue} value={timeValue}>
                                        {timeLabel}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>* Emergency Consultation</label>
                        <div className="radio-group emergency-group">
                            <label className={`radio-option-badge ${appointmentPayload.emergency === 'FALSE' ? 'active-std' : ''}`}>
                                <input
                                    type="radio"
                                    name="emergency"
                                    value="FALSE"
                                    checked={appointmentPayload.emergency === 'FALSE'}
                                    onChange={handleChangeAppointment('emergency')}
                                />
                                🟢 Standard
                            </label>
                            <label className={`radio-option-badge ${appointmentPayload.emergency === 'TRUE' ? 'active-emerg' : ''}`}>
                                <input
                                    type="radio"
                                    name="emergency"
                                    value="TRUE"
                                    checked={appointmentPayload.emergency === 'TRUE'}
                                    onChange={handleChangeAppointment('emergency')}
                                />
                                🔴 Emergency
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Card 3: Fee & Payment Summary */}
            <div className="payment-details-section">
                <div className="form-body-title">
                    <FaCreditCard size={15} color="#2563eb" /> Consultation Fee & Billing
                </div>
                <div className="form-row">
                    <div className="form-group inline-label-input">
                        <label>* Appointment Consultation Fee (₹)</label>
                        <input
                            type="text"
                            className="appointment-fee-input"
                            value={invoicePayload.totalAmount}
                            onChange={handleChangeInvoice('totalAmount')}
                            placeholder="Enter amount in ₹"
                        />
                    </div>
                </div>
            </div>

            {/* Primary Action Submit */}
            <div className="create-request-wrapper">
                <button className="create-request" onClick={handleScheduleAppointment}>
                    Schedule & Save Appointment
                </button>
            </div>
        </div>
    );
};

export default NewAppointment;
